/* ==========================================================================
 * Bespoky SimGym MB1 Fixes — Sprint 125 (May 18, 2026)
 * Companion JS for bespoky-simgym-fixes.css
 *
 * Implements:
 *  P0.1 Newsletter popup: delay 30s, one-shot per session, localStorage flag
 *  P0.2 Cart drawer: auto-open after add-to-cart + inject trust row
 *  P0.3 Quick Add: button on each .product-block opens fetch-based mini modal
 *  P0.4 Size Guide: ensure visible trigger on PDP (fallback if not present)
 *  P1.5 PDP Trust signal row: inject under .add-to-cart button
 *  P1.6 Cookie consent: persist dismissal in localStorage to prevent re-show
 *  P1.7 Color swatch hover preview: swap product-block image on hover
 *
 * Loaded via theme.liquid as defer, runs on DOMContentLoaded.
 * ========================================================================== */
(function () {
  'use strict';

  var LS = window.localStorage;
  var NS = 'bespoky_simgym_';

  function lsGet(k) { try { return LS.getItem(NS + k); } catch (e) { return null; } }
  function lsSet(k, v) { try { LS.setItem(NS + k, v); } catch (e) {} }

  /* ----------------------------------------------------------------------
     P0.1 — Newsletter popup behavior override
     - Suppress instant open: hide popup section until 30s elapsed
     - One-shot per session via lsSet('newsletter_dismissed', '1')
     - Replaces aggressive dismiss-day cookie with session-scoped storage
     ---------------------------------------------------------------------- */
  function tameNewsletterPopup() {
    if (lsGet('newsletter_dismissed') === '1') {
      // Mark as dismissed: hide any popup element that exists
      document.querySelectorAll('.cc-pop-up pop-up, .cc-pop-up .modal.popup-section').forEach(function (el) {
        el.style.display = 'none';
      });
      return;
    }

    // Wait for popup element to exist (Symmetry inserts late)
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      var popup = document.querySelector('pop-up.popup-section, .cc-pop-up pop-up');
      if (popup || attempts > 20) {
        clearInterval(iv);
        if (!popup) return;

        // Force close until 30s, then open
        if (typeof popup.close === 'function') {
          try { popup.close(); } catch (e) {}
        }
        popup.classList.add('invisible');
        popup.setAttribute('aria-hidden', 'true');

        setTimeout(function () {
          if (lsGet('newsletter_dismissed') === '1') return;
          if (typeof popup.open === 'function') {
            try { popup.open(); } catch (e) {}
          } else {
            popup.classList.remove('invisible');
            popup.setAttribute('aria-hidden', 'false');
          }
        }, 30000);

        // Track dismiss
        var closeBtn = popup.querySelector('.js-close-modal, .modal__close-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', function () {
            lsSet('newsletter_dismissed', '1');
          });
        }
        // Also catch submit-success path
        popup.addEventListener('submit', function () {
          lsSet('newsletter_dismissed', '1');
        }, true);
      }
    }, 250);
  }

  /* ----------------------------------------------------------------------
     P0.2 — Cart drawer: auto-open + trust row + free-shipping subtitle
     ---------------------------------------------------------------------- */
  function enhanceCartDrawer() {
    function injectTrust() {
      document.querySelectorAll('cart-drawer').forEach(function (drawer) {
        if (drawer.querySelector('.bespoky-cart-trust-row')) return;
        var checkoutBtns = drawer.querySelector('.checkout-buttons');
        if (!checkoutBtns) return;

        var shipNote = document.createElement('div');
        shipNote.className = 'bespoky-cart-shipping-note';
        shipNote.textContent = 'Free Worldwide Shipping Included';
        checkoutBtns.parentNode.insertBefore(shipNote, checkoutBtns);

        var row = document.createElement('div');
        row.className = 'bespoky-cart-trust-row';
        row.innerHTML = '' +
          '<span><span class="bespoky-trust-icon">✓</span> 30-Day Returns</span>' +
          '<span><span class="bespoky-trust-icon">✓</span> Lifetime Warranty</span>';
        checkoutBtns.parentNode.insertBefore(row, checkoutBtns.nextSibling);
      });
    }
    injectTrust();

    // Re-inject after drawer refreshes (Symmetry replaces innerHTML on update)
    var mo = new MutationObserver(function (mutations) {
      var needRe = false;
      mutations.forEach(function (m) {
        if (m.type === 'childList' && m.addedNodes.length) needRe = true;
      });
      if (needRe) injectTrust();
    });
    document.querySelectorAll('cart-drawer').forEach(function (d) {
      mo.observe(d, { childList: true, subtree: true });
    });

    // Hook add-to-cart fetch responses for auto-open (Symmetry already does this
    // when settings.after_add_to_cart === 'drawer', but force it here too).
    var origFetch = window.fetch;
    if (origFetch && !window.__bespokyFetchHooked) {
      window.__bespokyFetchHooked = true;
      window.fetch = function () {
        var url = arguments[0];
        var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
        var p = origFetch.apply(this, arguments);
        if (urlStr.indexOf('/cart/add') !== -1) {
          p.then(function (resp) {
            if (!resp || !resp.ok) return;
            // Open the cart drawer
            setTimeout(function () {
              var drawer = document.querySelector('cart-drawer');
              if (drawer && typeof drawer.open === 'function') {
                try { drawer.open(); } catch (e) {}
              } else if (drawer) {
                drawer.setAttribute('aria-hidden', 'false');
                drawer.classList.add('is-open');
              }
              injectTrust();
            }, 150);
          }).catch(function () {});
        }
        return p;
      };
    }
  }

  /* ----------------------------------------------------------------------
     P0.3 — Quick Add on product cards
     Inject button into each .product-block detail area. On click, fetch
     /products/{handle}.js, build mini variant picker, POST to /cart/add.
     ---------------------------------------------------------------------- */
  var qaModal = null;
  var qaState = { product: null, selectedVariantId: null, optionSelections: {} };

  function buildQaModal() {
    if (qaModal) return qaModal;
    qaModal = document.createElement('div');
    qaModal.className = 'bespoky-qa-modal';
    qaModal.setAttribute('role', 'dialog');
    qaModal.setAttribute('aria-modal', 'true');
    qaModal.innerHTML = '' +
      '<div class="bespoky-qa-modal__box">' +
      '  <button type="button" class="bespoky-qa-modal__close" aria-label="Close">×</button>' +
      '  <h3 class="bespoky-qa-modal__title"></h3>' +
      '  <div class="bespoky-qa-modal__price"></div>' +
      '  <div class="bespoky-qa-modal__options"></div>' +
      '  <button type="button" class="bespoky-qa-modal__atc">Add to Cart</button>' +
      '  <a href="#" class="bespoky-qa-modal__viewfull">View full product details</a>' +
      '  <div class="bespoky-qa-modal__error"></div>' +
      '  <div class="bespoky-qa-modal__success">Added to cart!</div>' +
      '</div>';
    document.body.appendChild(qaModal);

    qaModal.querySelector('.bespoky-qa-modal__close').addEventListener('click', closeQa);
    qaModal.addEventListener('click', function (e) {
      if (e.target === qaModal) closeQa();
    });
    qaModal.querySelector('.bespoky-qa-modal__atc').addEventListener('click', qaAddToCart);
    return qaModal;
  }

  function closeQa() {
    if (qaModal) qaModal.classList.remove('is-open');
  }

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat) || '${{amount}}';
    var dollars = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/g, dollars);
  }

  function recomputeVariant() {
    if (!qaState.product) return;
    var opts = qaState.product.options.map(function (o, i) {
      return qaState.optionSelections[i] || null;
    });
    var match = qaState.product.variants.find(function (v) {
      return opts.every(function (val, i) { return val !== null && v.options[i] === val; });
    });
    qaState.selectedVariantId = match ? match.id : null;
    var atc = qaModal.querySelector('.bespoky-qa-modal__atc');
    if (match && match.available) {
      atc.disabled = false;
      atc.textContent = 'Add to Cart — ' + formatMoney(match.price);
    } else if (match) {
      atc.disabled = true;
      atc.textContent = 'Sold out';
    } else {
      atc.disabled = true;
      atc.textContent = 'Select options';
    }
  }

  function renderQa(product) {
    buildQaModal();
    qaState.product = product;
    qaState.optionSelections = {};
    qaState.selectedVariantId = null;

    qaModal.querySelector('.bespoky-qa-modal__title').textContent = product.title || '';
    qaModal.querySelector('.bespoky-qa-modal__price').textContent = formatMoney(product.price || (product.variants[0] && product.variants[0].price) || 0);
    qaModal.querySelector('.bespoky-qa-modal__viewfull').setAttribute('href', '/products/' + product.handle);
    qaModal.querySelector('.bespoky-qa-modal__error').classList.remove('is-shown');
    qaModal.querySelector('.bespoky-qa-modal__success').classList.remove('is-shown');

    var optsEl = qaModal.querySelector('.bespoky-qa-modal__options');
    optsEl.innerHTML = '';

    var options = product.options || [];
    options.forEach(function (optName, optIdx) {
      var lbl = document.createElement('div');
      lbl.className = 'bespoky-qa-modal__opt-label';
      lbl.textContent = optName;
      optsEl.appendChild(lbl);

      var values = [];
      product.variants.forEach(function (v) {
        var val = v.options[optIdx];
        if (val && values.indexOf(val) === -1) values.push(val);
      });

      var grid = document.createElement('div');
      grid.className = 'bespoky-qa-modal__opts';
      values.forEach(function (val) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bespoky-qa-modal__opt';
        btn.textContent = val;
        btn.dataset.optIdx = String(optIdx);
        btn.dataset.optVal = val;
        // Check availability
        var anyAvail = product.variants.some(function (v) {
          return v.options[optIdx] === val && v.available;
        });
        if (!anyAvail) btn.classList.add('is-unavailable');

        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-unavailable')) return;
          // Deselect siblings
          grid.querySelectorAll('.bespoky-qa-modal__opt').forEach(function (b) {
            b.classList.remove('is-selected');
          });
          btn.classList.add('is-selected');
          qaState.optionSelections[optIdx] = val;
          recomputeVariant();
        });
        grid.appendChild(btn);
      });

      // Auto-select if only one option value
      if (values.length === 1) {
        qaState.optionSelections[optIdx] = values[0];
        var firstBtn = grid.querySelector('.bespoky-qa-modal__opt');
        if (firstBtn) firstBtn.classList.add('is-selected');
      }
      optsEl.appendChild(grid);
    });

    recomputeVariant();
    qaModal.classList.add('is-open');
  }

  function qaAddToCart() {
    if (!qaState.selectedVariantId) return;
    var atc = qaModal.querySelector('.bespoky-qa-modal__atc');
    var errEl = qaModal.querySelector('.bespoky-qa-modal__error');
    var sucEl = qaModal.querySelector('.bespoky-qa-modal__success');
    errEl.classList.remove('is-shown');
    sucEl.classList.remove('is-shown');
    var originalText = atc.textContent;
    atc.disabled = true;
    atc.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: qaState.selectedVariantId, quantity: 1 })
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw j;
        return j;
      });
    }).then(function () {
      sucEl.classList.add('is-shown');
      atc.textContent = 'Added ✓';
      setTimeout(function () {
        closeQa();
        // Trigger cart drawer refresh + open
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.refresh === 'function') {
          try { drawer.refresh(); } catch (e) {}
        }
        if (drawer && typeof drawer.open === 'function') {
          try { drawer.open(); } catch (e) {}
        }
      }, 600);
    }).catch(function (err) {
      atc.disabled = false;
      atc.textContent = originalText;
      errEl.textContent = (err && err.description) || (err && err.message) || 'Sorry, could not add to cart.';
      errEl.classList.add('is-shown');
    });
  }

  function attachQuickAdd() {
    document.querySelectorAll('product-block').forEach(function (card) {
      if (card.dataset.bespokyQaAttached) return;
      card.dataset.bespokyQaAttached = '1';

      // Find product handle from .product-link href
      var link = card.querySelector('a.product-link[href*="/products/"]');
      if (!link) return;
      var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;
      var handle = match[1];

      // Insert below detail block (inside .innerer)
      var innerer = card.querySelector('.innerer');
      if (!innerer) return;
      if (innerer.querySelector('.bespoky-quick-add')) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bespoky-quick-add';
      btn.textContent = 'Quick add +';
      btn.setAttribute('aria-label', 'Quick add this product');
      btn.dataset.handle = handle;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        btn.innerHTML = '<span class="bespoky-quick-add__spinner"></span>Loading';
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (product) {
            btn.textContent = 'Quick add +';
            renderQa(product);
          }).catch(function () {
            btn.textContent = 'Quick add +';
            window.location.href = '/products/' + handle;
          });
      });
      innerer.appendChild(btn);
    });
  }

  /* ----------------------------------------------------------------------
     P0.4 — Size Guide fallback trigger on PDP
     Only inject if main-product has no existing .size-chart-link.
     ---------------------------------------------------------------------- */
  function ensureSizeGuide() {
    if (document.body.classList.contains('template-product') === false &&
        !document.querySelector('.template-product, [class*="template-product"]')) {
      return;
    }
    // Symmetry already renders .size-chart-link when block.settings.enable_size_chart=true.
    // If absent, inject a fallback that opens our sizing-table snippet via fetch.
    var existing = document.querySelector('.size-chart-link, modal-opener[data-modal^="size-chart"]');
    if (existing) {
      // Just boost its visibility (CSS already strengthens it).
      return;
    }

    // Locate size selector area (variant-picker fieldset whose legend says Size)
    var sizeFieldset = null;
    document.querySelectorAll('variant-picker fieldset.option-selector').forEach(function (fs) {
      var legend = fs.querySelector('legend.label');
      if (legend && /size|beden|größe|taille|talla|misura/i.test(legend.textContent.trim())) {
        sizeFieldset = fs;
      }
    });
    if (!sizeFieldset) {
      // Try dropdown variant
      document.querySelectorAll('variant-picker .option-selector').forEach(function (sel) {
        var label = sel.querySelector('.label');
        if (label && /size|beden|größe|taille|talla|misura/i.test(label.textContent.trim())) {
          sizeFieldset = sel;
        }
      });
    }
    if (!sizeFieldset) return;

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'bespoky-size-guide-fallback';
    trigger.textContent = 'Size Guide';
    trigger.addEventListener('click', openSgFallback);
    sizeFieldset.parentNode.insertBefore(trigger, sizeFieldset.nextSibling);
  }

  function openSgFallback() {
    var modal = document.querySelector('.bespoky-sg-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'bespoky-sg-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.innerHTML = '' +
        '<div class="bespoky-sg-modal__box">' +
        '  <button type="button" class="bespoky-sg-modal__close" aria-label="Close">×</button>' +
        '  <h3>Size Guide</h3>' +
        '  <div class="bespoky-sg-modal__content"><p>Loading…</p></div>' +
        '</div>';
      document.body.appendChild(modal);
      modal.querySelector('.bespoky-sg-modal__close').addEventListener('click', function () {
        modal.classList.remove('is-open');
      });
      modal.addEventListener('click', function (e) {
        if (e.target === modal) modal.classList.remove('is-open');
      });

      // Fetch the size guide page (we know it exists as page.how-to-measure)
      fetch('/pages/how-to-measure')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          // Extract main content
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var main = doc.querySelector('.page-content, main#content, [class*="rte"]') || doc.body;
          modal.querySelector('.bespoky-sg-modal__content').innerHTML = main.innerHTML;
        }).catch(function () {
          modal.querySelector('.bespoky-sg-modal__content').innerHTML =
            '<p>Visit our <a href="/pages/how-to-measure">size guide</a> for measurements.</p>';
        });
    }
    modal.classList.add('is-open');
  }

  /* ----------------------------------------------------------------------
     P1.5 — PDP Trust Signal Row
     Inject under .add-to-cart button.
     ---------------------------------------------------------------------- */
  function injectProductTrust() {
    var atc = document.querySelector('button.add-to-cart, .add-to-cart');
    if (!atc) return;
    var atcRow = atc.closest('.quantity-submit-row') || atc.parentNode;
    if (!atcRow || atcRow.parentNode.querySelector('.bespoky-product-trust')) return;

    var iconCheck = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    var iconTruck = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="15" height="11"/><polygon points="16 8 20 8 23 11 23 17 16 17 16 8"/><circle cx="5.5" cy="20.5" r="2.5"/><circle cx="18.5" cy="20.5" r="2.5"/></svg>';
    var iconClock = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
    var iconReturn = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>';
    var iconShield = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';

    var row = document.createElement('div');
    row.className = 'bespoky-product-trust';
    row.innerHTML = '' +
      '<div class="bespoky-product-trust__item"><div class="bespoky-product-trust__icon">' + iconTruck + '</div><div class="bespoky-product-trust__label">Free Shipping</div><div>Worldwide</div></div>' +
      '<div class="bespoky-product-trust__item"><div class="bespoky-product-trust__icon">' + iconClock + '</div><div class="bespoky-product-trust__label">5–7 Days</div><div>Delivery</div></div>' +
      '<div class="bespoky-product-trust__item"><div class="bespoky-product-trust__icon">' + iconReturn + '</div><div class="bespoky-product-trust__label">30-Day</div><div>Returns</div></div>' +
      '<div class="bespoky-product-trust__item"><div class="bespoky-product-trust__icon">' + iconShield + '</div><div class="bespoky-product-trust__label">Lifetime</div><div>Warranty</div></div>';

    // Insert after the form (after the row containing ATC)
    var insertAfter = atcRow.closest('form') || atcRow;
    if (insertAfter.parentNode) {
      insertAfter.parentNode.insertBefore(row, insertAfter.nextSibling);
    }
  }

  /* ----------------------------------------------------------------------
     P1.6 — Cookie consent persistence
     Symmetry/Bespoky relies on Shopify's customer privacy API. We persist a
     localStorage flag and hide the banner on subsequent visits when it was
     previously dismissed/accepted.
     ---------------------------------------------------------------------- */
  function persistCookieBanner() {
    // Common selectors for cookie banners across Shopify themes & apps
    var selectors = [
      '#shopify-pc__banner',
      '.shopify-pc__banner',
      '[data-shopify-privacy-banner]',
      '.cookie-banner',
      '#cookieBanner',
      '.cookie-consent',
      '#cookie-notice'
    ];

    function hideAll() {
      selectors.forEach(function (s) {
        document.querySelectorAll(s).forEach(function (el) {
          el.style.display = 'none';
          el.setAttribute('aria-hidden', 'true');
        });
      });
    }

    if (lsGet('cookies_accepted') === '1') {
      hideAll();
      // Watch for late insertion (Shopify privacy banner can mount async)
      var obs = new MutationObserver(function () { hideAll(); });
      obs.observe(document.body, { childList: true, subtree: true });
      // Auto-disconnect after 10s
      setTimeout(function () { obs.disconnect(); }, 10000);
      return;
    }

    // Attach handler to detect dismissal/acceptance
    function attach() {
      selectors.forEach(function (s) {
        document.querySelectorAll(s).forEach(function (banner) {
          if (banner.dataset.bespokyAttached) return;
          banner.dataset.bespokyAttached = '1';
          banner.addEventListener('click', function (e) {
            var target = e.target;
            if (!target) return;
            // Capture any button click as accept/dismiss
            if (target.tagName === 'BUTTON' || target.closest('button, a')) {
              lsSet('cookies_accepted', '1');
              lsSet('cookies_accepted_ts', String(Date.now()));
            }
          }, true);
        });
      });
    }
    attach();
    var mo = new MutationObserver(attach);
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 15000);
  }

  /* ----------------------------------------------------------------------
     P1.7 — Color swatch variant preview on hover
     For each .product-block, intercept hover on .product-block-options__item
     (which has data-media). Find matching .product-block__image by data-media-id
     and toggle --active class.
     ---------------------------------------------------------------------- */
  function attachSwatchHover() {
    document.querySelectorAll('product-block').forEach(function (card) {
      if (card.dataset.bespokyHoverAttached) return;
      card.dataset.bespokyHoverAttached = '1';

      var swatches = card.querySelectorAll('.product-block-options--swatch .product-block-options__item[data-media]');
      if (!swatches.length) return;

      var images = card.querySelectorAll('.product-block__image');
      var originalActive = card.querySelector('.product-block__image--active');

      function activate(mediaId) {
        images.forEach(function (img) {
          if (img.dataset.mediaId === mediaId) {
            img.classList.add('product-block__image--active');
            img.style.opacity = '1';
          } else if (img !== originalActive) {
            img.classList.remove('product-block__image--active');
          }
        });
      }
      function reset() {
        images.forEach(function (img) {
          if (img === originalActive) {
            img.classList.add('product-block__image--active');
            img.style.opacity = '';
          } else {
            img.classList.remove('product-block__image--active');
            img.style.opacity = '';
          }
        });
      }

      swatches.forEach(function (sw) {
        var mediaId = sw.dataset.media;
        if (!mediaId) return;
        sw.addEventListener('mouseenter', function () { activate(mediaId); });
        sw.addEventListener('focus', function () { activate(mediaId); });
      });

      var optsContainer = card.querySelector('.product-block-options--swatch');
      if (optsContainer) {
        optsContainer.addEventListener('mouseleave', reset);
      }
    });
  }

  /* ----------------------------------------------------------------------
     Init
     ---------------------------------------------------------------------- */
  function init() {
    try { tameNewsletterPopup(); } catch (e) { console.warn('[bespoky-simgym] popup:', e); }
    try { enhanceCartDrawer(); } catch (e) { console.warn('[bespoky-simgym] cart:', e); }
    try { attachQuickAdd(); } catch (e) { console.warn('[bespoky-simgym] quickadd:', e); }
    try { ensureSizeGuide(); } catch (e) { console.warn('[bespoky-simgym] sizeguide:', e); }
    try { injectProductTrust(); } catch (e) { console.warn('[bespoky-simgym] trust:', e); }
    try { persistCookieBanner(); } catch (e) { console.warn('[bespoky-simgym] cookies:', e); }
    try { attachSwatchHover(); } catch (e) { console.warn('[bespoky-simgym] swatch:', e); }

    // Re-attach for dynamically-loaded product cards (infinite scroll, filters)
    var attachObs = new MutationObserver(function () {
      attachQuickAdd();
      attachSwatchHover();
    });
    attachObs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
